(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-rs-animated-menu]';
  var TRIGGER_SELECTOR = '.rs-reveal-menu__trigger';
  var LOCK_CLASS = 'rs-animated-menu-is-open';
  var REDUCE_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  var focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  window.RSAnimatedMenu = window.RSAnimatedMenu || {};
  window.RSAnimatedMenu.controllers = window.RSAnimatedMenu.controllers || {};

  function toArray(value) {
    return Array.prototype.slice.call(value || []);
  }

  function clamp(number, min, max) {
    return Math.max(min, Math.min(max, number));
  }

  function parseClampedInteger(value, fallback, min, max) {
    var parsed = parseInt(value, 10);

    if (Number.isNaN(parsed)) {
      parsed = fallback;
    }

    return clamp(parsed, min, max);
  }

  function isReducedMotion() {
    return window.matchMedia &&
      window.matchMedia(REDUCE_MOTION_QUERY).matches;
  }

  function isCoarsePointer() {
    return window.matchMedia &&
      window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  }

  function getData(root, name) {
    return root.getAttribute('data-' + name) || '';
  }

  function configureVideo(video) {
    if (!video) {
      return;
    }

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.controls = false;
    video.loop = false;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.removeAttribute('controls');
    video.removeAttribute('loop');
  }

  function setVideoSource(video, url, preload) {
    if (!video) {
      return;
    }

    configureVideo(video);
    video.pause();
    video.removeAttribute('src');
    video.preload = preload || 'metadata';
    video.load();

    if (url) {
      video.src = url;
      video.load();
    }
  }

  function resetVideo(video) {
    if (!video) {
      return;
    }

    video.pause();

    try {
      video.currentTime = 0;
    } catch (error) {
      // Some browsers reject currentTime changes before metadata is loaded.
    }

    video.removeAttribute('src');
    video.load();
  }

  function isAccessPopupActive() {
    if (
      document.documentElement.classList.contains('rs-access-popup-open') ||
      document.body.classList.contains('rs-access-popup-open')
    ) {
      return true;
    }

    return Boolean(
      document.querySelector('.rs-access-popup:not([hidden])[aria-hidden="false"]')
    );
  }

  function RSAnimatedMenu(root) {
    this.root = root;
    this.state = 'closed';
    this.activeProfile = null;
    this.lastTrigger = null;
    this.triggers = [];
    this.timers = [];
    this.openingVideoCleanup = null;
    this.closingVideoCleanup = null;
    this.videoReadinessCleanup = null;
    this.openingFadeCleanup = null;
    this.mediaStageAnimation = null;
    this.mediaStageRevealStartedAt = 0;
    this.videoSourceKind = '';
    this.videoSourceUrl = '';
    this.closeReady = false;
    this.linkNavigationTimer = null;
    this.linkNavigationLocked = false;

    this.mediaStage = root.querySelector('.rs-animated-menu__media-stage');
    this.holdImage = root.querySelector('.rs-animated-menu__hold-image');
    this.playbackVideo = root.querySelector('.rs-animated-menu__video--playback');
    this.openingVideo = this.playbackVideo;
    this.closingVideo = this.playbackVideo;
    this.navigation = root.querySelector('.rs-animated-menu__navigation');
    this.dismissButton = root.querySelector('.rs-animated-menu__dismiss');
    this.links = toArray(root.querySelectorAll('.rs-animated-menu__link'));

    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onTriggerPointerDown = this.onTriggerPointerDown.bind(this);
    this.onDismissClick = this.onDismissClick.bind(this);
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.onDocumentFocusin = this.onDocumentFocusin.bind(this);
    this.onMenuLinkClick = this.onMenuLinkClick.bind(this);
    this.onMenuLinkPointerDown = this.onMenuLinkPointerDown.bind(this);
    this.onMenuLinkPointerMove = this.onMenuLinkPointerMove.bind(this);
    this.onMenuLinkPointerUp = this.onMenuLinkPointerUp.bind(this);
    this.onMenuLinkPointerCancel = this.onMenuLinkPointerCancel.bind(this);
  }

  RSAnimatedMenu.prototype.init = function () {
    if (this.root.getAttribute('data-rs-animated-menu-initialized') === 'true') {
      return;
    }

    this.root.setAttribute('data-rs-animated-menu-initialized', 'true');
    configureVideo(this.playbackVideo);

    if (this.dismissButton) {
      this.dismissButton.addEventListener('click', this.onDismissClick);
    }

    this.bindMenuLinks();
    this.setNavigationVisible(false);
    this.refreshTriggers();
    this.queueMobileOpeningPrepare();
  };

  RSAnimatedMenu.prototype.destroy = function () {
    this.clearTimers();
    this.clearMediaListeners();
    this.cancelMediaStageAnimation(false);

    this.triggers.forEach(function (trigger) {
      trigger.removeEventListener('click', this.onTriggerClick, true);
      trigger.removeEventListener('pointerdown', this.onTriggerPointerDown, true);

      if (trigger.getAttribute('data-rs-animated-menu-original-controls') !== null) {
        var originalControls = trigger.getAttribute(
          'data-rs-animated-menu-original-controls'
        );

        if (originalControls) {
          trigger.setAttribute('aria-controls', originalControls);
        } else {
          trigger.removeAttribute('aria-controls');
        }
      }

      trigger.removeAttribute('data-rs-animated-menu-original-controls');
      trigger.removeAttribute('data-rs-animated-menu-bound');
    }, this);

    if (this.dismissButton) {
      this.dismissButton.removeEventListener('click', this.onDismissClick);
    }

    this.unbindMenuLinks();
    this.clearLinkInteractionState(true);
    resetVideo(this.playbackVideo);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    document.removeEventListener('focusin', this.onDocumentFocusin);
    this.unlockPage();
  };

  RSAnimatedMenu.prototype.isReady = function () {
    return this.root.getAttribute('data-enabled') === 'true' &&
      this.root.getAttribute('data-ready') === 'true' &&
      this.mediaStage &&
      this.holdImage &&
      this.playbackVideo &&
      this.navigation &&
      this.links.length > 0;
  };

  RSAnimatedMenu.prototype.refreshTriggers = function () {
    var nextTriggers = toArray(document.querySelectorAll(TRIGGER_SELECTOR));

    nextTriggers.forEach(function (trigger) {
      if (trigger.getAttribute('data-rs-animated-menu-bound') === 'true') {
        return;
      }

      trigger.setAttribute(
        'data-rs-animated-menu-original-controls',
        trigger.getAttribute('aria-controls') || ''
      );

      if (this.isReady()) {
        trigger.setAttribute('aria-controls', this.root.id);
        trigger.setAttribute('aria-expanded', 'false');
      }

      trigger.setAttribute('data-rs-animated-menu-bound', 'true');
      trigger.addEventListener('pointerdown', this.onTriggerPointerDown, true);
      trigger.addEventListener('click', this.onTriggerClick, true);
    }, this);

    this.triggers = nextTriggers;
  };

  RSAnimatedMenu.prototype.bindMenuLinks = function () {
    this.links.forEach(function (link) {
      if (link.getAttribute('data-rs-animated-menu-link-bound') === 'true') {
        return;
      }

      link.setAttribute('data-rs-animated-menu-link-bound', 'true');
      link.addEventListener('click', this.onMenuLinkClick);
      link.addEventListener('pointerdown', this.onMenuLinkPointerDown);
      link.addEventListener('pointermove', this.onMenuLinkPointerMove);
      link.addEventListener('pointerup', this.onMenuLinkPointerUp);
      link.addEventListener('pointercancel', this.onMenuLinkPointerCancel);
      link.addEventListener('lostpointercapture', this.onMenuLinkPointerCancel);
    }, this);
  };

  RSAnimatedMenu.prototype.unbindMenuLinks = function () {
    this.links.forEach(function (link) {
      link.removeEventListener('click', this.onMenuLinkClick);
      link.removeEventListener('pointerdown', this.onMenuLinkPointerDown);
      link.removeEventListener('pointermove', this.onMenuLinkPointerMove);
      link.removeEventListener('pointerup', this.onMenuLinkPointerUp);
      link.removeEventListener('pointercancel', this.onMenuLinkPointerCancel);
      link.removeEventListener('lostpointercapture', this.onMenuLinkPointerCancel);
      link.removeAttribute('data-rs-animated-menu-link-bound');
    }, this);
  };

  RSAnimatedMenu.prototype.isMenuSelectionFxEnabled = function () {
    if (
      isCoarsePointer() ||
      (this.activeProfile && this.activeProfile.isMobile) ||
      this.root.classList.contains('is-mobile')
    ) {
      return false;
    }

    return this.root.getAttribute('data-menu-selection-fx') !== 'false';
  };

  RSAnimatedMenu.prototype.getMenuClickDelay = function () {
    var delay = parseClampedInteger(
      getData(this.root, 'menu-click-delay'),
      200,
      120,
      280
    );

    return isReducedMotion() ? Math.min(delay, 100) : delay;
  };

  RSAnimatedMenu.prototype.isDelayableMenuLink = function (event, link) {
    var href = link.getAttribute('href');
    var target = (link.getAttribute('target') || '').toLowerCase();
    var url;

    if (!this.isMenuSelectionFxEnabled()) {
      return false;
    }

    if (
      event.defaultPrevented ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      (typeof event.button === 'number' && event.button !== 0)
    ) {
      return false;
    }

    if (
      !href ||
      href === '#' ||
      link.hasAttribute('download') ||
      (target && target !== '_self')
    ) {
      return false;
    }

    if (/^(javascript:|mailto:|tel:)/i.test(href)) {
      return false;
    }

    try {
      url = new URL(href, window.location.href);
    } catch (error) {
      return false;
    }

    return url.origin === window.location.origin;
  };

  RSAnimatedMenu.prototype.onMenuLinkPointerDown = function (event) {
    var link = event.currentTarget;

    link.removeAttribute('data-rs-animated-menu-press-canceled');

    if (isCoarsePointer()) {
      this.debugMenuLink('pointerdown:native-press', event, link);
      return;
    }

    if (
      !this.isMenuSelectionFxEnabled() ||
      this.state !== 'open' ||
      event.pointerType === 'mouse'
    ) {
      return;
    }

    link.setAttribute('data-rs-animated-menu-press-x', String(event.clientX));
    link.setAttribute('data-rs-animated-menu-press-y', String(event.clientY));
    link.removeAttribute('data-rs-animated-menu-press-canceled');
    link.classList.add('is-pressed');
  };

  RSAnimatedMenu.prototype.onMenuLinkPointerMove = function (event) {
    var link = event.currentTarget;
    var startX = parseFloat(link.getAttribute('data-rs-animated-menu-press-x'));
    var startY = parseFloat(link.getAttribute('data-rs-animated-menu-press-y'));
    var distance;

    if (!link.classList.contains('is-pressed')) {
      return;
    }

    distance = Math.hypot(event.clientX - startX, event.clientY - startY);

    if (distance > 10) {
      link.classList.remove('is-pressed');
      link.setAttribute('data-rs-animated-menu-press-canceled', 'true');
    }
  };

  RSAnimatedMenu.prototype.onMenuLinkPointerUp = function (event) {
    var link = event.currentTarget;

    this.debugMenuLink('pointerup', event, link);

    if (event.pointerType === 'mouse') {
      return;
    }

    window.setTimeout(function () {
      link.classList.remove('is-pressed');
    }, 90);
  };

  RSAnimatedMenu.prototype.onMenuLinkPointerCancel = function (event) {
    var link = event.currentTarget;

    link.classList.remove('is-pressed');

    if (!this.isMenuSelectionFxEnabled()) {
      link.removeAttribute('data-rs-animated-menu-press-canceled');
      this.debugMenuLink('pointercancel:native-cleared', event, link);
      return;
    }

    link.setAttribute('data-rs-animated-menu-press-canceled', 'true');
    this.debugMenuLink('pointercancel:marked-canceled', event, link);
  };

  RSAnimatedMenu.prototype.onMenuLinkClick = function (event) {
    var link = event.currentTarget;

    this.debugMenuLink('click:received', event, link);

    if (!this.isMenuSelectionFxEnabled()) {
      link.classList.remove('is-pressed', 'is-clicked', 'is-focused');
      link.removeAttribute('data-rs-animated-menu-press-canceled');
      this.debugMenuLink('click:native-allowed', event, link, {
        reason: 'mobile-or-selection-fx-disabled'
      });
      return;
    }

    if (link.getAttribute('data-rs-animated-menu-press-canceled') === 'true') {
      link.removeAttribute('data-rs-animated-menu-press-canceled');
      link.classList.remove('is-pressed');
      event.preventDefault();
      this.debugMenuLink('click:prevented', event, link, {
        reason: 'press-canceled'
      });
      return;
    }

    if (!this.isDelayableMenuLink(event, link)) {
      this.debugMenuLink('click:native-allowed', event, link, {
        reason: 'not-delayable'
      });
      return;
    }

    if (this.state !== 'open') {
      event.preventDefault();
      this.debugMenuLink('click:prevented', event, link, {
        reason: 'state-not-open'
      });
      return;
    }

    event.preventDefault();
    this.debugMenuLink('click:intercepted-desktop', event, link);

    if (this.linkNavigationLocked) {
      this.debugMenuLink('click:ignored', event, link, {
        reason: 'navigation-locked'
      });
      return;
    }

    this.startMenuLinkNavigation(link);
  };

  RSAnimatedMenu.prototype.startMenuLinkNavigation = function (link) {
    var delay = this.getMenuClickDelay();
    var href = link.href;

    this.clearLinkInteractionState(true);
    this.linkNavigationLocked = true;
    link.classList.remove('is-pressed');
    link.classList.add('is-clicked');

    if (this.navigation) {
      this.navigation.classList.add('is-navigating');
    }

    this.debugMenuLink('navigation:timeout-created', null, link, {
      delay: delay,
      href: href
    });

    this.linkNavigationTimer = window.setTimeout(function () {
      this.debugMenuLink('navigation:location-assign', null, link, {
        href: href
      });
      window.location.assign(href);
    }.bind(this), delay);
  };

  RSAnimatedMenu.prototype.clearLinkInteractionState = function (clearNavigationTimer) {
    if (clearNavigationTimer && this.linkNavigationTimer) {
      window.clearTimeout(this.linkNavigationTimer);
      this.linkNavigationTimer = null;
    }

    this.linkNavigationLocked = false;

    if (this.navigation) {
      this.navigation.classList.remove('is-navigating');
    }

    this.links.forEach(function (link) {
      link.classList.remove('is-pressed', 'is-clicked', 'is-focused');
      link.removeAttribute('data-rs-animated-menu-press-x');
      link.removeAttribute('data-rs-animated-menu-press-y');
      link.removeAttribute('data-rs-animated-menu-press-canceled');
    });
  };

  RSAnimatedMenu.prototype.onTriggerClick = function (event) {
    if (!this.isReady() || isAccessPopupActive()) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    this.open(event.currentTarget);
  };

  RSAnimatedMenu.prototype.onTriggerPointerDown = function () {
    if (!this.isReady() || isAccessPopupActive() || this.state !== 'closed') {
      return;
    }

    this.prepareMobileOpeningSource();
  };

  RSAnimatedMenu.prototype.onDismissClick = function () {
    this.close();
  };

  RSAnimatedMenu.prototype.onDocumentKeydown = function (event) {
    if (this.state === 'closed') {
      return;
    }

    if (event.key === 'Escape' || event.keyCode === 27) {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'Tab' || event.keyCode === 9) {
      this.trapFocus(event);
    }
  };

  RSAnimatedMenu.prototype.onDocumentFocusin = function (event) {
    if (this.state === 'closed' || this.root.contains(event.target)) {
      return;
    }

    this.focusFirst();
  };

  RSAnimatedMenu.prototype.getProfile = function () {
    var breakpoint = parseInt(getData(this.root, 'mobile-breakpoint'), 10);
    var isMobile = window.innerWidth <= (breakpoint || 767);

    return {
      isMobile: isMobile,
      opening: getData(
        this.root,
        isMobile ? 'mobile-opening-video' : 'desktop-opening-video'
      ),
      closing: getData(
        this.root,
        isMobile ? 'mobile-closing-video' : 'desktop-closing-video'
      ),
      hold: getData(
        this.root,
        isMobile ? 'mobile-hold-image' : 'desktop-hold-image'
      )
    };
  };

  RSAnimatedMenu.prototype.getOpeningMediaConfig = function () {
    return {
      fadeDuration: parseClampedInteger(
        getData(this.root, 'opening-media-fade-duration'),
        500,
        200,
        900
      ),
      preroll: parseClampedInteger(
        getData(this.root, 'opening-media-preroll'),
        80,
        0,
        250
      ),
      startBlur: parseClampedInteger(
        getData(this.root, 'opening-media-start-blur'),
        8,
        0,
        16
      ),
      mediaOpacity: parseClampedInteger(
        getData(this.root, 'menu-media-opacity'),
        100,
        70,
        100
      ) / 100
    };
  };

  RSAnimatedMenu.prototype.isVideoDebugEnabled = function () {
    return this.root.getAttribute('data-menu-video-debug') === 'true';
  };

  RSAnimatedMenu.prototype.debugVideo = function (eventName, detail) {
    if (!this.isVideoDebugEnabled() || !window.console || !console.log) {
      return;
    }

    var video = this.playbackVideo;
    var payload = detail || {};

    if (video) {
      payload.readyState = video.readyState;
      payload.networkState = video.networkState;
      payload.currentSrc = video.currentSrc || video.getAttribute('src') || '';
    }

    console.log('[rs-animated-menu:video] ' + eventName, payload);
  };

  RSAnimatedMenu.prototype.debugMenuLink = function (eventName, event, link, detail) {
    if (!this.isVideoDebugEnabled() || !window.console || !console.log) {
      return;
    }

    var payload = detail || {};

    payload.type = event && event.type ? event.type : eventName;
    payload.target = event && event.target && event.target.className ?
      String(event.target.className) :
      '';
    payload.href = link ? link.getAttribute('href') : '';
    payload.defaultPrevented = Boolean(event && event.defaultPrevented);
    payload.pointerType = event && event.pointerType ? event.pointerType : '';
    payload.state = this.state;
    payload.coarsePointer = isCoarsePointer();
    payload.selectionFxEnabled = this.isMenuSelectionFxEnabled();
    payload.navigationTimerActive = Boolean(this.linkNavigationTimer);

    console.log('[rs-animated-menu:link] ' + eventName, payload);
  };

  RSAnimatedMenu.prototype.clearVideoReadinessListener = function () {
    if (this.videoReadinessCleanup) {
      this.videoReadinessCleanup();
      this.videoReadinessCleanup = null;
    }
  };

  RSAnimatedMenu.prototype.setPlaybackSource = function (kind, url, preload, force) {
    var video = this.playbackVideo;
    var sameSource;

    if (!video || !url) {
      return false;
    }

    configureVideo(video);
    sameSource = this.videoSourceKind === kind &&
      this.videoSourceUrl === url &&
      Boolean(video.getAttribute('src'));

    if (sameSource && !force) {
      video.preload = preload || video.preload || 'metadata';
      this.debugVideo('source:reuse', {
        kind: kind,
        source: url
      });

      if (kind === 'closing' && video.readyState >= 2) {
        this.closeReady = true;
        this.root.setAttribute('data-close-ready', 'true');
      } else if (kind === 'closing') {
        this.watchClosingReadiness();
      }

      return true;
    }

    this.clearVideoReadinessListener();
    this.root.classList.remove('is-video-visible');
    this.closeReady = false;
    this.root.setAttribute('data-close-ready', 'false');

    video.pause();

    try {
      video.currentTime = 0;
    } catch (error) {
      // Metadata may not be available yet.
    }

    video.removeAttribute('src');
    video.preload = preload || 'metadata';
    video.load();

    video.src = url;
    this.videoSourceKind = kind;
    this.videoSourceUrl = url;
    video.load();

    this.debugVideo('source:set', {
      kind: kind,
      source: url,
      preload: video.preload
    });

    if (kind === 'closing') {
      this.watchClosingReadiness();
    }

    return true;
  };

  RSAnimatedMenu.prototype.queueMobileOpeningPrepare = function () {
    if (!this.isReady() || this.state !== 'closed') {
      return;
    }

    this.timers.push(window.setTimeout(function () {
      this.prepareMobileOpeningSource();
    }.bind(this), 160));
  };

  RSAnimatedMenu.prototype.prepareMobileOpeningSource = function () {
    var profile;

    if (!this.isReady() || this.state !== 'closed') {
      return;
    }

    profile = this.getProfile();

    if (!profile.isMobile || !profile.opening) {
      return;
    }

    this.setPlaybackSource('opening', profile.opening, 'auto', false);
  };

  RSAnimatedMenu.prototype.prepareClosingSource = function () {
    if (!this.activeProfile || !this.activeProfile.closing) {
      return false;
    }

    return this.setPlaybackSource(
      'closing',
      this.activeProfile.closing,
      'auto',
      false
    );
  };

  RSAnimatedMenu.prototype.watchClosingReadiness = function () {
    var video = this.playbackVideo;
    var ready;
    var cleanup;
    var onLoadedMetadata;

    this.clearVideoReadinessListener();
    this.closeReady = false;
    this.root.setAttribute('data-close-ready', 'false');

    if (!video) {
      return;
    }

    cleanup = function () {
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('loadeddata', ready);
      video.removeEventListener('canplay', ready);
    };

    onLoadedMetadata = function (event) {
      this.debugVideo('loadedmetadata', {
        kind: this.videoSourceKind,
        duration: video.duration
      });

      if (video.readyState >= 2) {
        ready(event);
      }
    }.bind(this);

    ready = function (event) {
      if (this.videoSourceKind !== 'closing') {
        return;
      }

      this.closeReady = true;
      this.root.setAttribute('data-close-ready', 'true');
      this.debugVideo('closing:ready', {
        event: event && event.type ? event.type : 'readyState'
      });

      cleanup();
    }.bind(this);

    if (video.readyState >= 2) {
      ready();
      return;
    }

    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('loadeddata', ready);
    video.addEventListener('canplay', ready);
    this.videoReadinessCleanup = cleanup;
  };

  RSAnimatedMenu.prototype.open = function (trigger) {
    if (this.state !== 'closed') {
      return;
    }

    this.activeProfile = this.getProfile();

    if (!this.activeProfile.hold) {
      return;
    }

    this.state = 'opening';
    this.lastTrigger = trigger || null;
    this.clearTimers();
    this.clearLinkInteractionState(true);
    this.root.hidden = false;
    this.root.setAttribute('aria-hidden', 'false');
    this.root.classList.remove(
      'is-opening',
      'is-open',
      'is-closing',
      'is-content-visible',
      'is-content-fading',
      'is-page-fade',
      'is-video-visible',
      'is-mounted',
      'is-visible',
      'is-mobile',
      'is-desktop'
    );
    this.root.classList.add(
      'is-mounted',
      'is-visible',
      this.activeProfile.isMobile ? 'is-mobile' : 'is-desktop'
    );

    this.resetMediaStageForOpening();
    this.holdImage.src = this.activeProfile.hold;
    this.setNavigationVisible(false);
    this.lockPage();
    this.setTriggersExpanded(true);

    document.addEventListener('keydown', this.onDocumentKeydown);
    document.addEventListener('focusin', this.onDocumentFocusin);

    this.queueFocus();

    if (isReducedMotion() || !this.activeProfile.opening) {
      this.showOpenState();
      return;
    }

    this.playOpeningVideo();
  };

  RSAnimatedMenu.prototype.playOpeningVideo = function () {
    var video = this.playbackVideo;

    if (!video || !this.activeProfile.opening) {
      this.showOpenState();
      return;
    }

    this.clearMediaListeners();
    this.root.classList.add('is-opening');

    if (!this.setPlaybackSource('opening', this.activeProfile.opening, 'auto', false)) {
      this.showOpenState();
      return;
    }

    try {
      video.currentTime = 0;
    } catch (error) {
      // Ignore seek failures and rely on fallback state.
    }

    this.root.classList.add('is-video-visible');
    this.waitForOpeningMediaFrame(video);

    var revealOnce = false;
    var finishOnce = false;
    var revealProgress = clamp(
      parseInt(getData(this.root, 'reveal-progress'), 10) || 70,
      0,
      100
    ) / 100;

    var reveal = function () {
      if (revealOnce || this.state !== 'opening') {
        return;
      }

      revealOnce = true;
      this.setNavigationVisible(true);
    }.bind(this);

    var finish = function () {
      if (finishOnce || this.state !== 'opening') {
        return;
      }

      finishOnce = true;
      this.clearOpeningVideoListener();
      reveal();
      this.finishOpening();
    }.bind(this);

    var onTimeUpdate = function () {
      if (
        video.duration &&
        Number.isFinite(video.duration) &&
        video.currentTime / video.duration >= revealProgress
      ) {
        reveal();
      }
    };

    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('ended', finish);
    this.openingVideoCleanup = function () {
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('ended', finish);
    };

    this.timers.push(window.setTimeout(reveal, 1200));
    this.timers.push(window.setTimeout(finish, 2600));

    this.debugVideo('opening:play-request', {
      source: this.activeProfile.opening,
      mobile: this.activeProfile.isMobile
    });

    var playPromise = video.play();

    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.then(function () {
        this.debugVideo('opening:play-resolve');
      }.bind(this)).catch(function (error) {
        this.debugVideo('opening:play-reject', {
          name: error && error.name ? error.name : 'error',
          message: error && error.message ? error.message : ''
        });
        this.clearOpeningVideoListener();
        this.showOpenState(true);
      }.bind(this));
    }
  };

  RSAnimatedMenu.prototype.waitForOpeningMediaFrame = function (video) {
    var done = false;
    var fallbackTimer = null;
    var frameCallbackId = null;
    var useVideoFrameCallback = video &&
      typeof video.requestVideoFrameCallback === 'function';
    var fallbackDelay = this.activeProfile && this.activeProfile.isMobile ? 250 : 120;
    var onLoadedData;
    var onPlaying;

    var cleanup = function () {
      if (fallbackTimer) {
        window.clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }

      if (
        frameCallbackId !== null &&
        video &&
        typeof video.cancelVideoFrameCallback === 'function'
      ) {
        video.cancelVideoFrameCallback(frameCallbackId);
      }

      if (video && !useVideoFrameCallback) {
        video.removeEventListener('loadeddata', onLoadedData);
        video.removeEventListener('playing', onPlaying);
      }
    };

    var ready = function () {
      if (done || this.state !== 'opening') {
        return;
      }

      done = true;
      cleanup();
      this.openingFadeCleanup = null;
      this.debugVideo('opening:first-frame');
      this.revealOpeningMediaStage(false);
    }.bind(this);

    onLoadedData = function () {
      this.debugVideo('opening:loadeddata');
      ready();
    }.bind(this);

    onPlaying = function () {
      this.debugVideo('opening:playing');
      ready();
    }.bind(this);

    this.clearOpeningFadeListener();

    if (!video) {
      ready();
      return;
    }

    if (useVideoFrameCallback) {
      frameCallbackId = video.requestVideoFrameCallback(ready);
    } else {
      video.addEventListener('loadeddata', onLoadedData);
      video.addEventListener('playing', onPlaying);
    }

    fallbackTimer = window.setTimeout(function () {
      this.debugVideo('opening:first-frame-fallback', {
        delay: fallbackDelay
      });
      ready();
    }.bind(this), fallbackDelay);
    this.openingFadeCleanup = function () {
      done = true;
      cleanup();
    };
  };

  RSAnimatedMenu.prototype.resetMediaStageForOpening = function () {
    if (!this.mediaStage) {
      return;
    }

    var config = this.getOpeningMediaConfig();
    var mobile = this.activeProfile && this.activeProfile.isMobile;
    this.cancelMediaStageAnimation(false);
    this.mediaStageRevealStartedAt = 0;
    this.mediaStage.style.transition = 'none';
    this.mediaStage.style.opacity = '0';
    this.mediaStage.style.filter = (isReducedMotion() || mobile) ?
      'none' :
      'blur(' + config.startBlur + 'px) brightness(0.88)';
    this.mediaStage.style.transform = (isReducedMotion() || mobile) ?
      'none' :
      'scale(1.015)';
  };

  RSAnimatedMenu.prototype.applyMediaStageFinal = function (opacity) {
    if (!this.mediaStage) {
      return;
    }

    var config = this.getOpeningMediaConfig();
    var finalOpacity = typeof opacity === 'number' ?
      opacity :
      config.mediaOpacity;

    this.mediaStage.style.transition = 'none';
    this.mediaStage.style.opacity = String(finalOpacity);
    this.mediaStage.style.filter = this.activeProfile && this.activeProfile.isMobile ?
      'none' :
      'blur(0px) brightness(1)';
    this.mediaStage.style.transform = this.activeProfile && this.activeProfile.isMobile ?
      'none' :
      'scale(1)';
  };

  RSAnimatedMenu.prototype.cancelMediaStageAnimation = function (keepCurrent) {
    if (!this.mediaStageAnimation) {
      return;
    }

    if (keepCurrent && this.mediaStage) {
      try {
        if (typeof this.mediaStageAnimation.commitStyles === 'function') {
          this.mediaStageAnimation.commitStyles();
        } else {
          var computed = window.getComputedStyle(this.mediaStage);
          this.mediaStage.style.opacity = computed.opacity;
          this.mediaStage.style.filter = computed.filter;
          this.mediaStage.style.transform = computed.transform;
        }
      } catch (error) {
        // If commitStyles is unavailable or rejected, keep the current inline state.
      }
    }

    this.mediaStageAnimation.cancel();
    this.mediaStageAnimation = null;
  };

  RSAnimatedMenu.prototype.revealOpeningMediaStage = function (skipPreroll) {
    var config = this.getOpeningMediaConfig();
    var delay = skipPreroll || isReducedMotion() ? 0 : config.preroll;

    var startReveal = function () {
      if (
        !this.mediaStage ||
        (this.state !== 'opening' && this.state !== 'open')
      ) {
        return;
      }

      this.runOpeningMediaStageAnimation();
    }.bind(this);

    if (delay > 0) {
      this.timers.push(window.setTimeout(startReveal, delay));
    } else {
      startReveal();
    }
  };

  RSAnimatedMenu.prototype.runOpeningMediaStageAnimation = function () {
    if (!this.mediaStage) {
      return;
    }

    var config = this.getOpeningMediaConfig();
    var reducedMotion = isReducedMotion();
    var mobile = this.activeProfile && this.activeProfile.isMobile;
    var duration = reducedMotion ?
      Math.min(config.fadeDuration, 100) :
      (mobile ? 350 : config.fadeDuration);
    var easing = mobile ?
      'cubic-bezier(0.2, 0.75, 0.25, 1)' :
      'cubic-bezier(0.16, 1, 0.3, 1)';
    var startFilter = (reducedMotion || mobile) ?
      'none' :
      'blur(' + config.startBlur + 'px) brightness(0.88)';
    var startTransform = (reducedMotion || mobile) ? 'none' : 'scale(1.015)';
    var endFilter = mobile ? 'none' : 'blur(0px) brightness(1)';
    var endTransform = mobile ? 'none' : 'scale(1)';
    var finalOpacity = config.mediaOpacity;

    this.cancelMediaStageAnimation(false);
    this.mediaStageRevealStartedAt = Date.now();
    this.mediaStage.style.transition = 'none';
    this.mediaStage.style.opacity = '0';
    this.mediaStage.style.filter = startFilter;
    this.mediaStage.style.transform = startTransform;

    if (this.mediaStage.animate) {
      this.mediaStageAnimation = this.mediaStage.animate(
        [
          {
            opacity: 0,
            filter: startFilter,
            transform: startTransform
          },
          {
            opacity: finalOpacity,
            filter: endFilter,
            transform: endTransform
          }
        ],
        {
          duration: duration,
          easing: easing,
          fill: 'forwards'
        }
      );

      this.mediaStageAnimation.onfinish = function () {
        this.applyMediaStageFinal(finalOpacity);
        this.cancelMediaStageAnimation(false);
      }.bind(this);

      return;
    }

    this.mediaStage.style.transition = [
      'opacity ' + duration + 'ms ' + easing,
      'filter ' + duration + 'ms ' + easing,
      'transform ' + duration + 'ms ' + easing
    ].join(', ');

    var setFallbackTarget = function () {
      this.mediaStage.style.opacity = String(finalOpacity);
      this.mediaStage.style.filter = endFilter;
      this.mediaStage.style.transform = endTransform;
    }.bind(this);

    this.timers.push(window.setTimeout(function () {
      this.applyMediaStageFinal(finalOpacity);
    }.bind(this), duration + 40));

    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(setFallbackTarget);
      });
    } else {
      window.setTimeout(setFallbackTarget, 32);
    }
  };

  RSAnimatedMenu.prototype.finishOpening = function () {
    if (this.state !== 'opening') {
      return;
    }

    this.state = 'open';
    this.root.classList.remove('is-opening', 'is-video-visible');
    this.root.classList.add('is-open');
    this.applyMediaStageFinal();

    if (this.playbackVideo) {
      this.playbackVideo.pause();

      try {
        this.playbackVideo.currentTime = 0;
      } catch (error) {
        // Metadata may not be available in every browser.
      }
    }

    this.prepareClosingSource();
  };

  RSAnimatedMenu.prototype.showOpenState = function (skipPreroll) {
    if (this.state === 'closed') {
      return;
    }

    var revealOpenState = function () {
      if (this.state === 'closed') {
        return;
      }

      this.setNavigationVisible(true);
      this.root.classList.remove('is-opening', 'is-closing', 'is-page-fade', 'is-video-visible');
      this.root.classList.add('is-mounted', 'is-visible', 'is-open');
      this.applyMediaStageFinal();
      this.state = 'open';

      if (this.playbackVideo) {
        this.clearOpeningVideoListener();
        resetVideo(this.playbackVideo);
      }

      this.prepareClosingSource();
    }.bind(this);

    var config = this.getOpeningMediaConfig();
    var contentDelay = isReducedMotion() ?
      Math.min(config.fadeDuration, 100) :
      config.fadeDuration + (skipPreroll ? 0 : config.preroll);

    this.clearTimers();
    this.clearOpeningFadeListener();
    this.revealOpeningMediaStage(Boolean(skipPreroll));

    if (contentDelay > 0) {
      this.timers.push(window.setTimeout(revealOpenState, contentDelay));
    } else {
      revealOpenState();
    }
  };

  RSAnimatedMenu.prototype.close = function () {
    if (this.state === 'closed' || this.state === 'closing') {
      return;
    }

    this.state = 'closing';
    this.clearTimers();
    this.clearMediaListeners();
    this.cancelMediaStageAnimation(true);
    this.clearLinkInteractionState(true);

    if (this.playbackVideo) {
      this.playbackVideo.pause();
    }

    this.root.classList.remove('is-opening', 'is-open', 'is-video-visible');
    this.root.classList.add('is-content-fading');
    this.setNavigationVisible(false);

    this.timers.push(window.setTimeout(function () {
      if (
        isReducedMotion() ||
        !this.activeProfile ||
        !this.activeProfile.closing
      ) {
        this.fallbackClose();
      } else {
        this.playClosingVideo();
      }
    }.bind(this), 180));
  };

  RSAnimatedMenu.prototype.playClosingVideo = function () {
    var video = this.playbackVideo;
    var waitTimer = null;
    var startPlayback;
    var waitCleanup;
    var waitReady;

    if (!video || !this.activeProfile || !this.activeProfile.closing) {
      this.fallbackClose();
      return;
    }

    this.clearMediaListeners();
    this.applyMediaStageFinal(1);
    this.root.classList.add('is-closing');
    this.root.classList.remove('is-video-visible');

    if (!this.setPlaybackSource('closing', this.activeProfile.closing, 'auto', false)) {
      this.fallbackClose();
      return;
    }

    startPlayback = function () {
      var finishOnce = false;
      var firstFrameShown = false;
      var frameCallbackId = null;
      var firstFrameTimer = null;
      var scheduleFinishFallback;
      var showFirstFrame;
      var finish;
      var onTimeUpdate;
      var onEnded;
      var onPlaying;

      this.clearClosingVideoListener();
      configureVideo(video);
      this.root.classList.remove('is-video-visible');

      try {
        video.currentTime = 0;
      } catch (error) {
        // Ignore seek failures and rely on fallback state.
      }

      showFirstFrame = function (reason) {
        if (firstFrameShown || this.state !== 'closing') {
          return;
        }

        firstFrameShown = true;
        this.root.classList.add('is-video-visible');
        this.debugVideo('closing:first-frame', {
          reason: reason
        });

        if (firstFrameTimer) {
          window.clearTimeout(firstFrameTimer);
          firstFrameTimer = null;
        }
      }.bind(this);

      finish = function () {
        if (finishOnce) {
          return;
        }

        finishOnce = true;
        this.debugVideo('closing:finish');
        this.clearClosingVideoListener();
        this.finishClosing();
      }.bind(this);

      onTimeUpdate = function () {
        var remaining;

        showFirstFrame('timeupdate');

        if (!video.duration || !Number.isFinite(video.duration)) {
          return;
        }

        remaining = video.duration - video.currentTime;

        if (remaining <= 0.14) {
          this.root.classList.add('is-page-fade');
        }

        if (remaining <= 0.08) {
          finish();
        }
      }.bind(this);

      onEnded = function () {
        this.debugVideo('closing:ended');
        finish();
      }.bind(this);

      onPlaying = function () {
        this.debugVideo('closing:playing');
        showFirstFrame('playing');
      }.bind(this);

      scheduleFinishFallback = function () {
        var duration = video.duration && Number.isFinite(video.duration) ?
          video.duration :
          1.2;
        var delay = Math.max(420, (duration * 1000) + 360);

        this.timers.push(window.setTimeout(function () {
          this.debugVideo('closing:duration-fallback', {
            delay: delay
          });
          finish();
        }.bind(this), delay));
      }.bind(this);

      video.addEventListener('timeupdate', onTimeUpdate);
      video.addEventListener('ended', onEnded);
      video.addEventListener('playing', onPlaying);
      this.closingVideoCleanup = function () {
        video.removeEventListener('timeupdate', onTimeUpdate);
        video.removeEventListener('ended', onEnded);
        video.removeEventListener('playing', onPlaying);

        if (
          frameCallbackId !== null &&
          typeof video.cancelVideoFrameCallback === 'function'
        ) {
          video.cancelVideoFrameCallback(frameCallbackId);
        }

        if (firstFrameTimer) {
          window.clearTimeout(firstFrameTimer);
          firstFrameTimer = null;
        }
      };

      if (typeof video.requestVideoFrameCallback === 'function') {
        frameCallbackId = video.requestVideoFrameCallback(function () {
          showFirstFrame('requestVideoFrameCallback');
        });
      }

      firstFrameTimer = window.setTimeout(function () {
        showFirstFrame('first-frame-fallback');
      }, 220);

      this.debugVideo('closing:play-request', {
        source: this.activeProfile.closing,
        mobile: this.activeProfile.isMobile
      });

      var playPromise = video.play();

      scheduleFinishFallback();

      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.then(function () {
          this.debugVideo('closing:play-resolve');
        }.bind(this)).catch(function (error) {
          this.debugVideo('closing:play-reject', {
            name: error && error.name ? error.name : 'error',
            message: error && error.message ? error.message : ''
          });
          this.clearClosingVideoListener();
          this.fallbackClose();
        }.bind(this));
      }
    }.bind(this);

    waitCleanup = function () {
      video.removeEventListener('loadeddata', waitReady);
      video.removeEventListener('canplay', waitReady);

      if (waitTimer) {
        window.clearTimeout(waitTimer);
        waitTimer = null;
      }
    };

    waitReady = function (event) {
      this.debugVideo('closing:ready-before-play', {
        event: event && event.type ? event.type : 'readyState'
      });
      waitCleanup();
      startPlayback();
    }.bind(this);

    if (this.closeReady || video.readyState >= 2) {
      waitReady();
      return;
    }

    video.addEventListener('loadeddata', waitReady);
    video.addEventListener('canplay', waitReady);
    waitTimer = window.setTimeout(function () {
      this.debugVideo('closing:ready-timeout', {
        delay: 300
      });
      waitCleanup();
      this.fallbackClose();
    }.bind(this), 300);

    this.closingVideoCleanup = waitCleanup;
  };

  RSAnimatedMenu.prototype.fallbackClose = function () {
    this.root.classList.add('is-page-fade');
    this.timers.push(window.setTimeout(function () {
      this.finishClosing();
    }.bind(this), 220));
  };

  RSAnimatedMenu.prototype.finishClosing = function () {
    this.clearTimers();
    this.clearMediaListeners();
    this.cancelMediaStageAnimation(false);
    resetVideo(this.playbackVideo);
    this.videoSourceKind = '';
    this.videoSourceUrl = '';
    this.closeReady = false;
    this.root.setAttribute('data-close-ready', 'false');

    this.root.classList.remove(
      'is-visible',
      'is-opening',
      'is-open',
      'is-closing',
      'is-video-visible',
      'is-content-visible',
      'is-content-fading',
      'is-page-fade',
      'is-mounted',
      'is-mobile',
      'is-desktop'
    );

    this.root.hidden = true;
    this.root.setAttribute('aria-hidden', 'true');
    this.holdImage.removeAttribute('src');
    this.resetMediaStageForOpening();
    this.activeProfile = null;
    this.mediaStageRevealStartedAt = 0;
    this.clearLinkInteractionState(true);
    this.state = 'closed';
    this.setTriggersExpanded(false);
    this.unlockPage();

    document.removeEventListener('keydown', this.onDocumentKeydown);
    document.removeEventListener('focusin', this.onDocumentFocusin);

    this.restoreFocus();
    this.queueMobileOpeningPrepare();
  };

  RSAnimatedMenu.prototype.setNavigationVisible = function (visible) {
    if (!this.navigation) {
      return;
    }

    this.navigation.setAttribute('aria-hidden', visible ? 'false' : 'true');

    this.links.forEach(function (link) {
      if (visible) {
        link.removeAttribute('tabindex');
      } else {
        link.setAttribute('tabindex', '-1');
      }
    });

    if (visible) {
      this.root.classList.add('is-content-visible');
      this.root.classList.remove('is-content-fading');
    } else {
      this.root.classList.remove('is-content-visible');
    }
  };

  RSAnimatedMenu.prototype.lockPage = function () {
    document.documentElement.classList.add(LOCK_CLASS);
    document.body.classList.add(LOCK_CLASS);
  };

  RSAnimatedMenu.prototype.unlockPage = function () {
    document.documentElement.classList.remove(LOCK_CLASS);
    document.body.classList.remove(LOCK_CLASS);
  };

  RSAnimatedMenu.prototype.setTriggersExpanded = function (expanded) {
    this.refreshTriggers();

    this.triggers.forEach(function (trigger) {
      if (this.isReady()) {
        trigger.setAttribute('aria-controls', this.root.id);
      }

      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }, this);
  };

  RSAnimatedMenu.prototype.getFocusableItems = function () {
    return toArray(this.root.querySelectorAll(focusableSelector)).filter(
      function (item) {
        if (item.tabIndex < 0) {
          return false;
        }

        return item.offsetParent !== null ||
          item === this.dismissButton;
      },
      this
    );
  };

  RSAnimatedMenu.prototype.trapFocus = function (event) {
    var items = this.getFocusableItems();

    if (!items.length) {
      event.preventDefault();
      return;
    }

    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  RSAnimatedMenu.prototype.focusFirst = function () {
    var items = this.getFocusableItems();

    if (!items.length) {
      return;
    }

    try {
      items[0].focus({ preventScroll: true });
    } catch (error) {
      items[0].focus();
    }
  };

  RSAnimatedMenu.prototype.queueFocus = function () {
    this.timers.push(window.setTimeout(function () {
      this.focusFirst();
    }.bind(this), 80));
  };

  RSAnimatedMenu.prototype.restoreFocus = function () {
    if (!this.lastTrigger || !document.contains(this.lastTrigger)) {
      return;
    }

    try {
      this.lastTrigger.focus({ preventScroll: true });
    } catch (error) {
      this.lastTrigger.focus();
    }
  };

  RSAnimatedMenu.prototype.clearTimers = function () {
    this.timers.forEach(function (timer) {
      window.clearTimeout(timer);
    });

    this.timers = [];
  };

  RSAnimatedMenu.prototype.clearOpeningVideoListener = function () {
    if (this.openingVideoCleanup) {
      this.openingVideoCleanup();
      this.openingVideoCleanup = null;
    }
  };

  RSAnimatedMenu.prototype.clearClosingVideoListener = function () {
    if (this.closingVideoCleanup) {
      this.closingVideoCleanup();
      this.closingVideoCleanup = null;
    }
  };

  RSAnimatedMenu.prototype.clearOpeningFadeListener = function () {
    if (this.openingFadeCleanup) {
      this.openingFadeCleanup();
      this.openingFadeCleanup = null;
    }
  };

  RSAnimatedMenu.prototype.clearMediaListeners = function () {
    this.clearOpeningVideoListener();
    this.clearClosingVideoListener();
    this.clearOpeningFadeListener();
    this.clearVideoReadinessListener();
  };

  function initRoot(root) {
    if (!root || !root.id) {
      return;
    }

    if (window.RSAnimatedMenu.controllers[root.id]) {
      window.RSAnimatedMenu.controllers[root.id].refreshTriggers();
      return;
    }

    var controller = new RSAnimatedMenu(root);
    window.RSAnimatedMenu.controllers[root.id] = controller;
    controller.init();
  }

  function initAll() {
    toArray(document.querySelectorAll(ROOT_SELECTOR)).forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector(ROOT_SELECTOR);

    if (root) {
      initRoot(root);
      return;
    }

    Object.keys(window.RSAnimatedMenu.controllers).forEach(function (id) {
      window.RSAnimatedMenu.controllers[id].refreshTriggers();
    });
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector(ROOT_SELECTOR);

    if (!root || !window.RSAnimatedMenu.controllers[root.id]) {
      return;
    }

    window.RSAnimatedMenu.controllers[root.id].destroy();
    delete window.RSAnimatedMenu.controllers[root.id];
  });
})();
